import axios from 'axios';
import { tokenUtils } from '../utils/tokenUtils';

const API_URL = process.env.REACT_APP_API_URL + '/Posts';
const PUBLIC_API_URL = `${API_URL}/public`;

const buildPostsParams = (params = {}, options = {}) => {
  const { allowOnlyCollection = true, allowLikesSort = true } = options;
  const nextParams = {};

  if (allowOnlyCollection && typeof params.onlyCollection === 'boolean') {
    nextParams.onlyCollection = params.onlyCollection;
  }

  if (allowOnlyCollection && typeof params.onlyLiked === 'boolean') {
    nextParams.onlyLiked = params.onlyLiked;
  }

  if (params.labelIds && Array.isArray(params.labelIds) && params.labelIds.length > 0) {
    nextParams.labelIds = params.labelIds.join(',');
  } else if (typeof params.labelIds === 'string' && params.labelIds.trim()) {
    nextParams.labelIds = params.labelIds;
  }

  if (typeof params.category === 'string' && params.category.trim()) {
    nextParams.category = params.category;
  }

  if (typeof params.isPublic === 'boolean') {
    nextParams.isPublic = params.isPublic;
  }

  if (typeof params.sortBy === 'string' && params.sortBy.trim()) {
    if (allowLikesSort) {
      nextParams.sortBy = params.sortBy;
    } else if (params.sortBy !== 'likes' && params.sortBy !== 'mostLikes' && params.sortBy !== 'masLikes') {
      nextParams.sortBy = params.sortBy;
    }
  }

  return nextParams;
};

function getAuthHeaders(isMultipart = false) {
  const token = tokenUtils.get();
  if (!token) throw new Error('No se encontró token de autenticación');
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  if (!isMultipart) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
}

const handleAuthError = (error) => {
  if (error.response?.status === 401) {
    tokenUtils.clearAuth();
    window.location.href = '/';
  }
};

/** Convierte rutas relativas del API en URL absoluta servible por el frontend. */
const resolveMediaUrl = (path) => {
  if (!path || typeof path !== 'string') return '';
  const trimmed = path.trim();
  if (!trimmed) return '';
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('blob:')
  ) {
    return trimmed;
  }
  const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return `${process.env.REACT_APP_LOCATION || ''}${cleanPath}`;
};

const normalizePostMetrics = (post = {}) => {
  const views = Number(post.views ?? post.Views ?? 0) || 0;
  const likes = Number(post.likes ?? post.Likes ?? 0) || 0;
  const savedCount = Number(
    post.savedCount ?? post.SavedCount ?? post.collectionCount ?? post.CollectionCount ?? 0
  ) || 0;
  const engagementScore = Number(
    post.engagementScore ?? post.EngagementScore ?? views + likes + savedCount
  ) || 0;

  const rawThumbnail = post.thumbnail ?? post.Thumbnail ?? post.thumbnailPath ?? '';
  const rawVideoUrl = post.videoUrl ?? post.VideoUrl ?? post.path ?? post.Path ?? '';
  const rawFileUrl = post.fileUrl ?? post.FileUrl ?? post.path ?? post.Path ?? '';

  return {
    ...post,
    views,
    likes,
    savedCount,
    engagementScore,
    thumbnail: resolveMediaUrl(rawThumbnail) || rawThumbnail || '',
    videoUrl: resolveMediaUrl(rawVideoUrl) || rawVideoUrl || '',
    fileUrl: resolveMediaUrl(rawFileUrl) || rawFileUrl || '',
  };
};

const normalizePostsList = (data) => {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.items)
        ? data.items
        : [];
  return list.map(normalizePostMetrics);
};

// Obtener todos los posts
const getPosts = async (params = {}) => {
  try {
    const response = await axios.get(API_URL, {
      headers: getAuthHeaders(),
      params: buildPostsParams(params),
    });
    return normalizePostsList(response.data);
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener posts:', error);
    throw error;
  }
};

/** Top publicaciones por tendencia (views + likes + guardados). */
const getTrendingPosts = async (limit = 5) => {
  try {
    const response = await axios.get(`${API_URL}/trending`, {
      headers: getAuthHeaders(),
      params: { limit },
    });
    return normalizePostsList(response.data);
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener posts en tendencia:', error);
    throw error;
  }
};

// Obtener posts públicos (sin autenticación)
const getPublicPosts = async (params = {}) => {
  try {
    const response = await axios.get(PUBLIC_API_URL, {
      params: buildPostsParams(params, { allowOnlyCollection: false, allowLikesSort: false }),
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener posts públicos:', error);
    throw error;
  }
};

// Obtener un post por ID
const getPostById = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/${id}`, {
      headers: getAuthHeaders(),
    });
    return normalizePostMetrics(response.data || {});
  } catch (error) {
    handleAuthError(error);
    console.error('Error al obtener post por ID:', error);
    throw error;
  }
};

// Obtener un post público por ID (sin autenticación)
const getPublicPostById = async (id) => {
  try {
    const response = await axios.get(`${PUBLIC_API_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener post público por ID:', error);
    throw error;
  }
};

// Registrar vista de un post
const registerPostView = async (id) => {
  try {
    const response = await axios.post(`${API_URL}/${id}/view`, null, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al registrar vista del post:', error);
    throw error;
  }
};

// Agregar post a la colección del usuario
const addPostToCollection = async (id) => {
  try {
    const response = await axios.post(`${API_URL}/${id}/collection`, null, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al agregar post a colección:', error);
    throw error;
  }
};

// Quitar post de la colección del usuario
const removePostFromCollection = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/${id}/collection`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al quitar post de colección:', error);
    throw error;
  }
};

// Agregar like a un post
const addPostLike = async (id) => {
  try {
    const response = await axios.post(`${API_URL}/${id}/like`, null, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al dar like al post:', error);
    throw error;
  }
};

// Quitar like de un post
const removePostLike = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/${id}/like`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al quitar like del post:', error);
    throw error;
  }
};

// Crear un nuevo post
const createPost = async (formData) => {
  try {
    const postFormData = new FormData();
    const isPublicValue = typeof formData.isPublic === 'boolean'
      ? formData.isPublic
      : formData.IsPublic;
    
    // Agregar thumbnail como archivo si existe
    if (formData.thumbnail && formData.thumbnail instanceof File) {
      postFormData.append('thumbnail', formData.thumbnail);
    }
    
    // Agregar los demás campos
    if (formData.title) {
      postFormData.append('title', formData.title);
    }
    
    if (formData.description) {
      postFormData.append('description', formData.description);
    }
    
    if (formData.category) {
      postFormData.append('category', formData.category);
    }
    
    if (formData.teacherId) {
      postFormData.append('teacherId', formData.teacherId);
    }
    
    if (formData.videoId) {
      postFormData.append('videoId', formData.videoId);
    }
    
    if (formData.documentId) {
      postFormData.append('documentId', formData.documentId);
    }

    if (formData.courseId) {
      postFormData.append('courseId', formData.courseId);
    } else {
      postFormData.append('courseId', '');
    }
    
    // Agregar labelIds como array (solo IDs numéricos válidos)
    if (formData.labelIds && Array.isArray(formData.labelIds)) {
      formData.labelIds
        .map((labelId) => Number(labelId))
        .filter((labelId) => Number.isFinite(labelId) && labelId > 0)
        .forEach((labelId) => {
          postFormData.append('LabelIds', String(labelId));
        });
    }

    if (isPublicValue !== undefined && isPublicValue !== null) {
      postFormData.append('IsPublic', String(isPublicValue));
    }
    
    const response = await axios.post(API_URL, postFormData, {
      headers: getAuthHeaders(true),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al crear post:', error);
    throw error;
  }
};

// Actualizar un post
const updatePost = async (id, formData) => {
  try {
    const postFormData = new FormData();
    const isPublicValue = typeof formData.isPublic === 'boolean'
      ? formData.isPublic
      : formData.IsPublic;
    
    // Agregar thumbnail como archivo si existe
    if (formData.thumbnail && formData.thumbnail instanceof File) {
      postFormData.append('thumbnail', formData.thumbnail);
    }
    
    // Agregar los demás campos
    if (formData.title) {
      postFormData.append('title', formData.title);
    }
    
    if (formData.description) {
      postFormData.append('description', formData.description);
    }
    
    if (formData.category) {
      postFormData.append('category', formData.category);
    }
    
    if (formData.teacherId) {
      postFormData.append('teacherId', formData.teacherId);
    }
    
    if (formData.videoId) {
      postFormData.append('videoId', formData.videoId);
    }
    
    if (formData.documentId) {
      postFormData.append('documentId', formData.documentId);
    }

    if (formData.courseId) {
      postFormData.append('courseId', formData.courseId);
    } else {
      postFormData.append('courseId', '');
    }
    
    // Agregar labelIds como array (solo IDs numéricos válidos)
    if (formData.labelIds && Array.isArray(formData.labelIds)) {
      formData.labelIds
        .map((labelId) => Number(labelId))
        .filter((labelId) => Number.isFinite(labelId) && labelId > 0)
        .forEach((labelId) => {
          postFormData.append('LabelIds', String(labelId));
        });
    }

    if (isPublicValue !== undefined && isPublicValue !== null) {
      postFormData.append('IsPublic', String(isPublicValue));
    }
    
    const response = await axios.put(`${API_URL}/${id}`, postFormData, {
      headers: getAuthHeaders(true),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al actualizar post:', error);
    throw error;
  }
};

// Inscribirse al curso vinculado al post
const enrollLinkedCourse = async (postId) => {
  try {
    const response = await axios.post(`${API_URL}/${postId}/enroll-course`, {}, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al inscribirse al curso del post:', error);
    throw error;
  }
};

// Eliminar un post
const deletePost = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    handleAuthError(error);
    console.error('Error al eliminar post:', error);
    throw error;
  }
};

const postsService = {
  getPosts,
  getTrendingPosts,
  getPublicPosts,
  getPublicPostById,
  getPostById,
  registerPostView,
  addPostToCollection,
  removePostFromCollection,
  addPostLike,
  removePostLike,
  createPost,
  updatePost,
  enrollLinkedCourse,
  deletePost,
  normalizePostMetrics,
  resolveMediaUrl,
};

export default postsService;
